import {
  DonutChart,
  MultiSeriesChart,
  Series,
  SingleSeriesChart,
} from "@/pages/data/DataChart";
import { Play } from "@/queries/plays";
import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import Variablepie from "highcharts/modules/variable-pie";
import { capitalize, groupBy, sumBy, uniq } from "lodash";

if (typeof Highcharts === "object" && typeof window !== "undefined") {
  HighchartsMore(Highcharts);
  Variablepie(Highcharts);
  // Stock(Highcharts);
  // NoDataToDisplay(Highcharts);
  // SolidGauge(Highcharts);
  // Heatmap(Highcharts);
}

export function PlaylistReport({ plays }: { plays: Play[] }) {
  // Helper function to determine play status
  const getPlayStatus = (play: Play): string => {
    if (play.completed_at) {
      return "Completed";
    }
    if (play.actioned_at) return "Actioned";
    if (play.scheduled_at) return "Scheduled";
    return "Pending";
  };

  // New Chart: Plays by Hour
  const preparePlaysByHour = () => {
    // Group plays by hour based on start_interval
    const playsByHour = new Map<string, number>();

    // Initialize all hours with 0
    for (let hour = 0; hour < 24; hour++) {
      const hourStr = hour.toString().padStart(2, "0") + ":00";
      playsByHour.set(hourStr, 0);
    }

    // Count plays by hour using start_interval
    plays.forEach((play) => {
      // Calculate hour directly from interval (6 intervals = 1 hour)
      const hour = Math.floor(play.start_interval / 6);
      const hourStr = hour.toString().padStart(2, "0") + ":00";
      playsByHour.set(hourStr, (playsByHour.get(hourStr) || 0) + 1);
    });

    const labels = Array.from(playsByHour.keys());
    const values = Array.from(playsByHour.values());

    return {
      labels,
      values,
      xAxisTitle: "Time",
      yAxisTitle: "Number of Plays",
    };
  };

  // Chart 2: Play status summary (unchanged)
  const prepareStatusLocationChart = () => {
    const statuses = ["Pending", "Scheduled", "Actioned", "Completed"];
    const statusGroups = groupBy(plays, getPlayStatus);
    const locationTypes = uniq(plays.map((p) => p.location_type));

    const series: Series[] = locationTypes.map((locType) => ({
      name: capitalize(locType.toLowerCase()),
      // Ensure we map through all possible statuses
      values: statuses.map(
        (status) =>
          (statusGroups[status] || []).filter(
            (p) => p.location_type === locType
          ).length
      ),
    }));

    return {
      series,
      labels: statuses,
      xAxisTitle: "Status",
      yAxisTitle: "Number of Plays",
    };
  };

  // Chart 3: Play Status Distribution (corrected labels)
  const prepareStatusDonut = () => {
    const statusCounts = groupBy(plays, getPlayStatus);
    const statuses = ["Pending", "Scheduled", "Actioned", "Completed"];

    const values = statuses.map(
      (status) => (statusCounts[status] || []).length
    );

    return {
      values,
      labels: statuses as any, // Type workaround: component uses labels as names despite interface type
      centerLabel: plays.length.toString(),
      centerLabelSubText: "Pain Points",
    };
  };

  // Chart 4: Passenger Impact (corrected labels and type safety)
  const preparePassengerImpact = () => {
    const playTypeGroups = groupBy(plays, "title");

    const values = Object.values(playTypeGroups).map(
      (group: Play[]) => group.length
    );
    const labels = Object.keys(playTypeGroups); // Play titles as labels
    const radii = Object.values(playTypeGroups).map((group: Play[]) =>
      sumBy(group, "observed_value")
    );

    return {
      values,
      labels: labels as any, // Type workaround: component uses labels as names despite interface type
      radii,
    };
  };

  const playsByHourData = preparePlaysByHour();
  const statusLocationData = prepareStatusLocationChart();
  const statusDonutData = prepareStatusDonut();
  const passengerImpactData = preparePassengerImpact();

  return (
    <div className="space-y-8 p-6">
      {/* Chart 1: Plays by Hour */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Plays by Hour</h2>
        <SingleSeriesChart {...playsByHourData} type="column" height={300} />
      </div>

      {/* Chart 2: Status Summary */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Play Status Summary</h2>
        <MultiSeriesChart
          {...statusLocationData}
          chartType="column"
          stacked={true}
          height={300}
        />
      </div>

      {/* Chart 3: Status Distribution */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Play Status Distribution</h2>
        <DonutChart {...statusDonutData} height={300} />
      </div>

      {/* Chart 4: Passenger Impact */}
      <div>
        <h2 className="text-xl font-semibold mb-4">
          Passenger Impact by Play Type
        </h2>
        <DonutChart {...passengerImpactData} height={300} />
      </div>
    </div>
  );
}
